import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Divider from 'src/components/ui/Divider/Divider';
import Input from 'src/components/ui/Input/Input';
import {
  ForecastPlanningSettings as ForecastPlanningSettingsDto,
  useForecastPlanningSettingsQuery,
} from 'src/services/forecastPlanningSettings';
import styles from './ForecastPlanningSettings.module.scss';

// The form keeps every field as a string for display in the (read-only)
// inputs; values are parsed only to render the validity/preview hints.
type ForecastPlanningForm = {
  planning_deadline_day: string;
  reminder_days: string;
  period_lookahead_months: string;
};

// Portal-locked lookahead: the customer cannot change this without an
// explicit OK from WeShip — admins set higher values via the Odoo backend.
const LOCKED_LOOKAHEAD = 2;

const defaultForm: ForecastPlanningForm = {
  planning_deadline_day: '20',
  reminder_days: '14, 7, 3',
  period_lookahead_months: String(LOCKED_LOOKAHEAD),
};

const fromServer = (s: ForecastPlanningSettingsDto): ForecastPlanningForm => ({
  planning_deadline_day: String(s.planning_deadline_day ?? 20),
  reminder_days: (s.reminder_days ?? []).join(', '),
  period_lookahead_months: String(s.period_lookahead_months ?? LOCKED_LOOKAHEAD),
});

// Parse a comma-separated string into a deduplicated, descending list of
// positive integers — mirrors the backend's reminder-day parsing.
const parseReminderDays = (raw: string): number[] => {
  const seen = new Set<number>();
  raw
    .split(',')
    .map(part => parseInt(part.trim(), 10))
    .filter(n => Number.isFinite(n) && n > 0)
    .forEach(n => seen.add(n));
  return Array.from(seen).sort((a, b) => b - a);
};

interface ForecastPlanningSettingsProps {
  hasWriteAccess?: boolean;
}

// The whole Forecast-Planung tab is read-only in the portal: customers can
// view their current planning configuration but changes are made on the
// WeShip/backend side, so every field is disabled and there is no save action.
const ForecastPlanningSettings: React.FC<ForecastPlanningSettingsProps> = () => {
  const { t } = useTranslation();

  const { data: settings } = useForecastPlanningSettingsQuery();

  const [form, setForm] = useState<ForecastPlanningForm>(defaultForm);

  useEffect(() => {
    if (settings) setForm(fromServer(settings));
  }, [settings]);

  const update = <K extends keyof ForecastPlanningForm>(key: K, value: ForecastPlanningForm[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const deadlineDay = parseInt(form.planning_deadline_day, 10);
  const reminderDays = parseReminderDays(form.reminder_days);

  const deadlineDayValid = Number.isFinite(deadlineDay) && deadlineDay >= 1 && deadlineDay <= 28;
  const reminderDaysValid = reminderDays.length > 0;

  return (
    <div className={classNames(styles.root)}>
      <div className={styles.base}>
        <div className={styles.label}>
          <span className="typo-text-16--bold text-full block">{t('settings.forecast_planning.deadline_day')}</span>
          <span className="typo-text-12 text-faded mt-4 block">
            {t('settings.forecast_planning.deadline_day_description')}
          </span>
        </div>
        <div className={styles.control}>
          <Input
            type="number"
            value={form.planning_deadline_day}
            onChange={e => update('planning_deadline_day', e.target.value)}
            placeholder="20"
            disabled
            className="w-[160px]"
          />
          {!deadlineDayValid && (
            <span className={styles.errorHint}>{t('settings.forecast_planning.deadline_day_invalid')}</span>
          )}
        </div>
      </div>

      <Divider />

      <div className={styles.base}>
        <div className={styles.label}>
          <span className="typo-text-16--bold text-full block">{t('settings.forecast_planning.reminder_days')}</span>
          <span className="typo-text-12 text-faded mt-4 block">
            {t('settings.forecast_planning.reminder_days_description')}
          </span>
        </div>
        <div className={styles.control}>
          <Input
            value={form.reminder_days}
            onChange={e => update('reminder_days', e.target.value)}
            placeholder="14, 7, 3"
            disabled
          />
          {reminderDaysValid ? (
            <span className={styles.hint}>
              {t('settings.forecast_planning.reminder_days_preview', { days: reminderDays.join(', ') })}
            </span>
          ) : (
            <span className={styles.errorHint}>{t('settings.forecast_planning.reminder_days_invalid')}</span>
          )}
        </div>
      </div>

      <Divider />

      <div className={styles.base}>
        <div className={styles.label}>
          <span className="typo-text-16--bold text-full block">{t('settings.forecast_planning.lookahead')}</span>
          <span className="typo-text-12 text-faded mt-4 block">
            {t('settings.forecast_planning.lookahead_description')}
          </span>
        </div>
        <div className={styles.control}>
          <Input
            type="number"
            value={String(LOCKED_LOOKAHEAD)}
            onChange={() => {
              /* locked — see hint */
            }}
            placeholder={String(LOCKED_LOOKAHEAD)}
            disabled
            className="w-[160px]"
          />
          <span className={styles.hint}>{t('settings.forecast_planning.lookahead_locked_hint')}</span>
        </div>
      </div>
    </div>
  );
};

export default ForecastPlanningSettings;
